/**
 * Cart Page component
 * Handles cart page functionality including:
 * - Order note toggle behavior
 * - Gift message toggle behavior
 * - Form state management
 */

class CartPage {
  constructor(container = document) {
    this.container = container
    this.orderNoteToggle = container.querySelector('.cart-page__order-note-toggle')
    this.orderNoteForm = container.querySelector('.cart-page__order-note-form')
    
    // Gift message elements
    this.giftMessageContainer = container.querySelector('.maybourne-cart-gift-message')
    this.giftMessageCheckbox = container.querySelector('.maybourne-cart-gift-message__checkbox')
    this.giftMessageForm = container.querySelector('.maybourne-cart-gift-message__form')
    this.giftMessageTextarea = container.querySelector('.maybourne-cart-gift-message__textarea')
    this.giftMessageCounter = container.querySelector('.maybourne-cart-gift-message__count')
    this.giftMessageHiddenInput = container.querySelector('#cart-gift-message-value')
    
    this.init()
  }
  
  init() {
    this.bindEvents()
    this.initGiftMessage()
  }
  
  bindEvents() {
    if (this.orderNoteToggle) {
      this.orderNoteToggle.addEventListener('click', this.handleToggleClick.bind(this))
    }
    
    // Gift message events
    if (this.giftMessageCheckbox) {
      this.giftMessageCheckbox.addEventListener('change', this.handleGiftMessageToggle.bind(this))
    }
    
    if (this.giftMessageTextarea) {
      this.giftMessageTextarea.addEventListener('input', this.handleGiftMessageInput.bind(this))
      this.giftMessageTextarea.addEventListener('paste', this.handleGiftMessagePaste.bind(this))
    }
  }
  
  initGiftMessage() {
    if (this.giftMessageCounter) {
      this.updateGiftMessageCounter()
    }
    
    // Debounce the cart update function
    this.debouncedUpdateCartAttributes = this.debounce(this.updateCartAttributes.bind(this), 500);
  }
  
  // Debounce utility
  debounce(func, wait) {
    let timeout;
    return function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }
  
  handleToggleClick(event) {
    event.preventDefault()
    
    const isExpanded = this.orderNoteToggle.getAttribute('aria-expanded') === 'true'
    
    if (isExpanded) {
      this.hideForm()
    } else {
      this.showForm()
    }
  }
  
  showForm() {
    this.orderNoteToggle.setAttribute('aria-expanded', 'true')
    this.orderNoteForm.removeAttribute('hidden')
  }
  
  hideForm() {
    this.orderNoteToggle.setAttribute('aria-expanded', 'false')
    this.orderNoteForm.setAttribute('hidden', '')
  }
  
  // Gift message methods
  handleGiftMessageToggle() {
    if (this.giftMessageCheckbox.checked) {
      this.showGiftMessageForm()
    } else {
      this.hideGiftMessageForm()
    }
  }
  
  showGiftMessageForm() {
    if (this.giftMessageForm) {
      this.giftMessageForm.style.display = 'block'
      // Focus the textarea after a short delay for better UX
      setTimeout(() => {
        if (this.giftMessageTextarea) {
          this.giftMessageTextarea.focus()
        }
      }, 100)
    }
  }
  
  hideGiftMessageForm() {
    if (this.giftMessageForm) {
      this.giftMessageForm.style.display = 'none'
      // Clear the textarea and hidden input when hiding
      if (this.giftMessageTextarea) {
        this.giftMessageTextarea.value = ''
      }
      if (this.giftMessageHiddenInput) {
        this.giftMessageHiddenInput.value = ''
      }
      this.updateGiftMessageCounter()
      this.updateCartAttributes(); // Immediately clear attribute on hide
    }
  }
  
  handleGiftMessageInput() {
    this.updateGiftMessageHiddenInput()
    this.updateGiftMessageCounter()
    this.debouncedUpdateCartAttributes(); // Use debounced update
  }
  
  handleGiftMessagePaste() {
    setTimeout(() => {
      this.updateGiftMessageHiddenInput()
      this.updateGiftMessageCounter()
      this.debouncedUpdateCartAttributes(); // Use debounced update
    }, 10)
  }
  
  updateGiftMessageHiddenInput() {
    if (this.giftMessageHiddenInput && this.giftMessageTextarea) {
      this.giftMessageHiddenInput.value = this.giftMessageTextarea.value;

      // Manually dispatch a 'change' event on the hidden input.
      // This helps notify any other scripts (like an Ajax cart handler) that the form has changed
      // and needs to be re-serialized before submission.
      const event = new Event('change', { bubbles: true });
      this.giftMessageHiddenInput.dispatchEvent(event);
    }
  }
  
  updateGiftMessageCounter() {
    if (!this.giftMessageCounter || !this.giftMessageTextarea) return
    
    const currentLength = this.giftMessageTextarea.value.length
    this.giftMessageCounter.textContent = currentLength
    
    // Add visual feedback when approaching limit
    if (currentLength > 240) {
      this.giftMessageCounter.style.color = '#e74c3c'
    } else if (currentLength > 200) {
      this.giftMessageCounter.style.color = '#f39c12'
    } else {
      this.giftMessageCounter.style.color = 'var(--color-text-secondary)'
    }
  }

  // New method to update cart attributes via AJAX
  updateCartAttributes() {
    const message = this.giftMessageTextarea ? this.giftMessageTextarea.value : '';

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'gift_message': message
        }
      })
    })
    .then(response => response.json())
    .catch((error) => {
      // console.error('Error updating cart attributes:', error);
    });
  }

  destroy() {
    // Clean up event listeners
    if (this.orderNoteToggle) {
      this.orderNoteToggle.removeEventListener('click', this.handleToggleClick.bind(this))
    }
    
    // Gift message cleanup
    if (this.giftMessageCheckbox) {
      this.giftMessageCheckbox.removeEventListener('change', this.handleGiftMessageToggle.bind(this))
    }
    
    if (this.giftMessageTextarea) {
      this.giftMessageTextarea.removeEventListener('input', this.handleGiftMessageInput.bind(this))
      this.giftMessageTextarea.removeEventListener('paste', this.handleGiftMessagePaste.bind(this))
    }
  }
}

export default CartPage 